import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, errMessage } from "@/lib/api";
import { mediaUrl } from "@/lib/media";

const RadioCtx = createContext(null);
export const useRadio = () => useContext(RadioCtx);

export function RadioProvider({ children }) {
  const audioRef = useRef(null);
  const pendingSeek = useRef(null);
  const [tracks, setTracks] = useState([]);
  const [idx, setIdx] = useState(0);
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [resume, setResume] = useState(null);

  useEffect(() => {
    const a = new Audio();
    a.preload = "metadata";
    a.crossOrigin = "anonymous";
    audioRef.current = a;
    try {
      const r = JSON.parse(localStorage.getItem("wh_radio_resume") || "null");
      if (r && r.song_id) setResume(r);
    } catch { /* ignore */ }
    return () => { a.pause(); a.src = ""; };
  }, []);

  const ensureLoaded = useCallback(async () => {
    if (loaded || loading) return;
    setLoading(true);
    try {
      const res = await api.get("/radio/playlist");
      setTracks(res.data.tracks || []);
      setLoaded(true);
    } catch (e) { toast.error(errMessage(e)); }
    finally { setLoading(false); }
  }, [loaded, loading]);

  const track = tracks[idx];
  const resumeIndex = resume ? tracks.findIndex((t) => t.song_id === resume.song_id) : -1;

  // Pre-position on the last-listened track before play is pressed.
  useEffect(() => {
    if (!started && resumeIndex >= 0) setIdx(resumeIndex);
  }, [resumeIndex, started]);

  // Load & autoplay on track change once started.
  useEffect(() => {
    const a = audioRef.current;
    if (!a || !track) return;
    a.src = mediaUrl(track.audio);
    a.load();
    if (started) a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    // eslint-disable-next-line
  }, [idx, tracks]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCur(a.currentTime);
    const onMeta = () => {
      setDur(a.duration);
      if (pendingSeek.current != null) {
        try { a.currentTime = Math.min(pendingSeek.current, (a.duration || 0) - 0.5); } catch { /* ignore */ }
        pendingSeek.current = null;
      }
    };
    const onEnd = () => setIdx((i) => (tracks.length ? (i + 1) % tracks.length : 0));
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, [tracks.length]);

  // Persist resume position.
  useEffect(() => {
    if (!started || !track) return;
    const write = () => {
      const a = audioRef.current;
      localStorage.setItem("wh_radio_resume", JSON.stringify({
        song_id: track.song_id, user_id: track.user_id, name: track.name,
        title: track.title, time: a?.currentTime || 0,
      }));
    };
    write();
    const id = setInterval(write, 4000);
    return () => { write(); clearInterval(id); };
  }, [started, track]);

  const startRadio = (mode) => {
    if (!tracks.length) return;
    setStarted(true);
    const a = audioRef.current;
    if (mode === "resume" && resumeIndex >= 0) {
      setIdx(resumeIndex);
      const t = resume?.time || 0;
      if (a) {
        if (a.readyState >= 1 && a.duration) { try { a.currentTime = Math.min(t, a.duration - 0.5); } catch { /* ignore */ } }
        else pendingSeek.current = t;
      }
    } else {
      setIdx(Math.floor(Math.random() * tracks.length));
    }
    if (a) a.play().then(() => setPlaying(true)).catch(() => {});
  };

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (!started) return startRadio(resumeIndex >= 0 ? "resume" : "shuffle");
    if (playing) { a.pause(); setPlaying(false); }
    else a.play().then(() => setPlaying(true)).catch(() => {});
  };

  const next = () => setIdx((i) => (tracks.length ? (i + 1) % tracks.length : 0));
  const prev = () => setIdx((i) => (tracks.length ? (i - 1 + tracks.length) % tracks.length : 0));
  const reshuffle = () => {
    setTracks((t) => { const c = [...t]; for (let i = c.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [c[i], c[j]] = [c[j], c[i]]; } return c; });
    setIdx(0);
    toast("Reshuffled the mix");
  };
  const seekRatio = (ratio) => {
    const a = audioRef.current;
    if (a && a.duration) a.currentTime = Math.min(1, Math.max(0, ratio)) * a.duration;
  };
  const stop = () => {
    const a = audioRef.current;
    if (a) a.pause();
    setPlaying(false);
    setStarted(false);
  };

  const progress = dur ? (cur / dur) * 100 : 0;

  return (
    <RadioCtx.Provider value={{
      tracks, idx, setIdx, track, started, playing, cur, dur, progress, loading, loaded,
      resume, resumeIndex, ensureLoaded, startRadio, toggle, next, prev, reshuffle, seekRatio, stop,
    }}>
      {children}
    </RadioCtx.Provider>
  );
}
